import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  Brain,
  BookOpen,
  FileText,
  Puzzle,
  ListOrdered,
  Flag,
  Trophy,
  Clock,
  HelpCircle,
  Grid,
  X,
  Keyboard,
  Filter,
} from "lucide-react";
import { formatDisplayTitle } from "../../lib/formatTitle";
import { SubjectBadge } from "../../components/ui/Badges";
import { GeneratedContent } from "../../components/GeneratedContent";
import { MathText } from "../../components/MathText";
import { QuestionFigure } from "../../components/QuestionFigure";
import { QuestionPassageLayout } from "../../components/student/QuestionPassageLayout";
import {
  clearCachedSession,
  clearQuizSession,
  loadQuizSession,
  saveQuizAnswers,
  saveQuizSession,
  type QuizQuestion,
} from "../../lib/quizSession";
import { slugifySubject } from "../../lib/studySubjects";
import { useAuth } from "../../context/AuthContext";
import { useStudent } from "../../context/StudentContext";
import { api, ApiError } from "../../lib/api";
import { mapApiError } from "../../components/ui/StatusMessage";
import { Modal } from "../../components/ui/Modal";
import { FocusShell } from "../../components/ui/FocusShell";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";

function useQuizRoutes() {
  return {
    home: "/student/study",
    play: "/student/study/quiz/play",
    results: "/student/study/quiz/results",
  };
}
interface ReportQuestionModalProps {
  questionId: string;
  questionText: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ReportQuestionModal({
  questionId,
  questionText,
  isOpen,
  onClose,
  onSuccess,
}: ReportQuestionModalProps) {
  const { tokens, getAccessToken } = useAuth();
  const [selectedReason, setSelectedReason] = useState<string>("Wrong answer key");
  const [customReason, setCustomReason] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const PRESET_REASONS = [
    "Wrong answer key / incorrect answer indicated",
    "Ambiguous or confusing question phrasing",
    "Typo, grammatical error, or formatting issue",
    "Incorrect or misleading explanation",
    "Other issue",
  ];

  if (!isOpen) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const token = tokens?.accessToken || (await getAccessToken());
    if (!token) return;
    const finalReason =
      selectedReason === "Other issue"
        ? customReason.trim()
        : customReason.trim()
        ? `${selectedReason}: ${customReason.trim()}`
        : selectedReason;

    if (!finalReason || finalReason.length < 3) {
      setError("Please describe the issue in at least 3 characters.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await api.generate.reportQuestion(token, questionId, finalReason);
      setSubmitted(true);
      if (onSuccess) onSuccess();
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setCustomReason("");
      }, 1500);
    } catch (err: any) {
      setError(err instanceof ApiError ? mapApiError(err).message : "We could not send your report. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="sm"
      title="Report question"
      className="p-6"
    >
      <div className="space-y-4">

        {submitted ? (
          <div className="py-6 text-center space-y-2">
            <CheckCircle size={36} className="text-success mx-auto" />
            <p className="font-bold text-foreground text-sm">Report submitted</p>
            <p className="text-xs text-muted-foreground">
              This question has been flagged for review.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-muted/40 p-3 rounded-xl border border-border/50 text-xs text-muted-foreground line-clamp-2">
              {questionText}
            </div>

            {error && (
              <div className="p-2.5 rounded-lg bg-destructive/10 text-destructive text-xs font-medium border border-destructive/30">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs font-bold text-foreground block">Problem type</label>
              <div className="space-y-1.5">
                {PRESET_REASONS.map((reason) => (
                  <label
                    key={reason}
                    className={`flex items-center gap-2.5 p-2.5 rounded-xl border text-xs cursor-pointer transition ${
                      selectedReason === reason
                        ? "border-primary bg-primary/5 text-primary font-semibold"
                        : "border-border hover:bg-muted text-foreground"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportReason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={() => setSelectedReason(reason)}
                      className="text-primary focus:ring-primary h-3.5 w-3.5"
                    />
                    <span>{reason}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground block">
                Additional details (optional)
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Describe what is incorrect or what the right answer should be..."
                className="w-full text-xs p-3 rounded-xl border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none h-20"
              />
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border border-border font-semibold text-xs hover:bg-muted transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2.5 rounded-md bg-primary hover:opacity-90 text-on-accent font-semibold text-xs transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />}
                <span>Submit report</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

export function QuizPage() {
  const navigate = useNavigate();
  const routes = useQuizRoutes();
  const session = useMemo(() => loadQuizSession(), []);

  const { tokens } = useAuth();
  const token = tokens?.accessToken;
  const [current, setCurrent] = useState(session?.currentQuestion ?? 0);
  const [userAnswers, setUserAnswers] = useState<(number | null)[]>([]);
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showNavigator, setShowNavigator] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [reportingQ, setReportingQ] = useState<QuizQuestion | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const isSubmittedRef = useRef(false);

  // Timer support
  const totalTimeSeconds = useMemo(() => {
    if (session?.timeLimitMinutes) return session.timeLimitMinutes * 60;
    if (session?.duration) return session.duration * 60;
    if (session?.source === "exam") return (session?.questions?.length || 20) * 90;
    return null;
  }, [session]);

  const [timeLeft, setTimeLeft] = useState<number | null>(totalTimeSeconds);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t === null || t <= 1) {
          clearInterval(interval);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  const timerAnnouncement =
    timeLeft === 300 ? "5 minutes remaining" : timeLeft === 60 ? "1 minute remaining" : "";

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  useEffect(() => {
    if (!session?.questions?.length) {
      navigate(routes.home, { replace: true });
    } else {
      const initial = new Array(session.questions.length).fill(null);
      if (session.answers?.length === session.questions.length) {
        session.answers.forEach((ans, idx) => {
          if (ans != null && ans >= 0) initial[idx] = ans;
        });
      }
      setUserAnswers(initial);
      if (session.currentQuestion != null && session.currentQuestion < session.questions.length) {
        setCurrent(session.currentQuestion);
      }
    }
  }, [session, navigate, routes.home]);

  const questions = session?.questions ?? [];
  const answeredCount = userAnswers.filter((a) => a !== null).length;

  // Unsaved answers beforeunload guard
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isSubmittedRef.current && answeredCount > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [answeredCount]);

  const changeQuestion = useCallback(
    (idx: number) => {
      setCurrent(idx);
      const numericAnswers = userAnswers.map((a) => (a === null ? -1 : a));
      saveQuizAnswers(numericAnswers, idx);
    },
    [userAnswers],
  );

  const selectOption = useCallback(
    (idx: number) => {
      setUserAnswers((prev) => {
        const next = [...prev];
        next[current] = idx;
        const numericAnswers = next.map((a) => (a === null ? -1 : a));
        saveQuizAnswers(numericAnswers, current);
        return next;
      });
    },
    [current],
  );

  const toggleFlag = useCallback((idx: number) => {
    setFlaggedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  function handleTriggerSubmit() {
    setShowConfirmModal(true);
  }

  function handleLeaveQuiz() {
    const numericAnswers = userAnswers.map((a) => (a === null ? -1 : a));
    saveQuizAnswers(numericAnswers, current);
    navigate(routes.home);
  }

  const q = questions[current];

  // Keyboard navigation shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        showConfirmModal ||
        showLeaveConfirm ||
        showShortcutsModal ||
        showNavigator ||
        reportingQ
      ) {
        return;
      }

      if (e.key >= "1" && e.key <= "4" && q?.options) {
        const optIdx = parseInt(e.key, 10) - 1;
        if (optIdx < q.options.length) {
          selectOption(optIdx);
        }
      } else if (["a", "b", "c", "d"].includes(e.key.toLowerCase()) && q?.options) {
        const optIdx = e.key.toLowerCase().charCodeAt(0) - 97;
        if (optIdx < q.options.length) {
          selectOption(optIdx);
        }
      } else if (e.key === "ArrowLeft") {
        if (current > 0) changeQuestion(current - 1);
      } else if (e.key === "ArrowRight") {
        if (current < questions.length - 1) changeQuestion(current + 1);
      } else if (e.key === "Enter") {
        if (current < questions.length - 1) changeQuestion(current + 1);
        else handleTriggerSubmit();
      } else if (e.key.toLowerCase() === "f") {
        toggleFlag(current);
      } else if (e.key === "?") {
        setShowShortcutsModal((v) => !v);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    current,
    questions.length,
    q?.options,
    showConfirmModal,
    showLeaveConfirm,
    showShortcutsModal,
    showNavigator,
    reportingQ,
    selectOption,
    changeQuestion,
    toggleFlag,
  ]);

  if (!session?.questions?.length || !q) return null;

  const selected = userAnswers[current] ?? null;

  async function finalizeAndNavigate() {
    isSubmittedRef.current = true;
    setSubmissionError(null);
    const numericAnswers = userAnswers.map((a) => (a === null ? -1 : a));
    saveQuizAnswers(numericAnswers, current);

    // Persist attempt to database
    if (token && questions.length) {
      try {
        const persistence = session?.examId
          ? api.generate.submitPracticeExam(token, {
            examId: session.examId,
            subject: session.subject || questions[0]?.subject,
            title: session.title,
            totalQuestions: questions.length,
            answers: numericAnswers,
            questions: questions,
          })
          : api.generate.submitQuiz(token, {
            subject: session?.subject || questions[0]?.subject || "General",
            courseId: session?.courseId,
            moduleId: session?.moduleId,
            chapterId: session?.chapterId,
            topicId: session?.topicId,
            title: session?.title,
            difficulty: session?.difficulty,
            totalQuestions: questions.length,
            answers: numericAnswers,
            questions: questions,
          })
        const outcomes = questions.map((question, i) => {
          const userAns = numericAnswers[i];
          return { questionId: question.id, isCorrect: userAns >= 0 && userAns === question.correct };
        });
        await Promise.all([persistence, api.generate.recordQuizOutcomes(token, outcomes)]);
      } catch (err) {
        isSubmittedRef.current = false;
        setSubmissionError(err instanceof ApiError ? mapApiError(err).message : "We could not save your results. Please try submitting again.");
        return;
      }
    }

    if (session?.subject) {
      clearCachedSession(`quiz:${session.subject}`);
    }
    if (session?.title) {
      clearCachedSession(`quiz:${session.title}`);
    }

    const subjectSlug =
      session?.subjectSlug ||
      (session?.subject ? slugifySubject(session.subject) : "") ||
      (questions[0]?.subject ? slugifySubject(questions[0].subject) : "");

    navigate(routes.results, {
      state: {
        answers: numericAnswers,
        title: session?.title || "Exam",
        subjectSlug,
        subject: session?.subject,
      },
    });
  }

  const qType = (q.questionType || "multiple_choice").toLowerCase();
  const passageId = q.passageId || q.passage_id;
  const currentPassage = passageId && session.passages ? session.passages[passageId] : null;
  const isCurrentFlagged = flaggedQuestions.has(current);

  return (
    <FocusShell bare title={session.title || "Exam"} onClose={() => setShowLeaveConfirm(true)} closeLabel="Leave quiz">
    <div className={`${currentPassage ? "max-w-6xl" : "max-w-4xl"} mx-auto space-y-6 px-4 pb-16 pt-2`}>
      {submissionError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {submissionError}
        </div>
      )}
      {/* ── Screen Reader Announcements ── */}
      {timerAnnouncement && (
        <div aria-live="polite" className="sr-only">
          {timerAnnouncement}
        </div>
      )}

      {/* ── Sticky Top Bar with Progress, Timer, Controls ── */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border -mx-4 px-4 sm:mx-0 sm:px-5 py-3 rounded-b-lg sm:rounded-lg shadow-xs">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              onClick={() => setShowLeaveConfirm(true)}
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition"
              title="Leave quiz"
              aria-label="Leave quiz"
            >
              <X size={18} />
            </button>
            <div className="min-w-0">
              <h2 className="text-sm sm:text-base font-bold font-display text-foreground truncate max-w-xs sm:max-w-sm">
                {formatDisplayTitle(session.title) ||
                  (session.source === "exam" ? "Practice exam" : "Practice quiz")}
              </h2>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">
                  Question {current + 1} of {questions.length}
                </span>
                <span>•</span>
                <span>{answeredCount} answered</span>
                {flaggedQuestions.size > 0 && (
                  <>
                    <span>•</span>
                    <span className="text-primary font-medium">
                      {flaggedQuestions.size} flagged
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Countdown timer if applicable */}
            {timeLeft !== null && (
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono font-bold border ${
                  timeLeft <= 60
                    ? "bg-destructive/10 border-destructive/30 text-destructive animate-pulse"
                    : timeLeft <= 300
                    ? "bg-primary/10 border-primary/25 text-primary"
                    : "bg-muted border-border text-foreground"
                }`}
                title="Time remaining"
              >
                <Clock size={13} />
                <span>{formatTime(timeLeft)}</span>
              </div>
            )}

            {/* Flag question toggle */}
            <button
              type="button"
              onClick={() => toggleFlag(current)}
              className={`p-2 rounded-md border text-xs font-semibold transition flex items-center gap-1.5 ${
                isCurrentFlagged
                  ? "bg-primary text-on-accent border-primary"
                  : "bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              title={isCurrentFlagged ? "Unflag question" : "Flag question for review"}
              aria-label="Flag question for review"
            >
              <Flag size={14} className={isCurrentFlagged ? "fill-current" : ""} />
              <span className="hidden sm:inline">
                {isCurrentFlagged ? "Flagged" : "Flag"}
              </span>
            </button>

            {/* Question navigator modal toggle */}
            <button
              type="button"
              onClick={() => setShowNavigator(true)}
              className="p-2 rounded-md border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted text-xs font-semibold transition flex items-center gap-1.5"
              title="Open question grid"
              aria-label="Open question grid"
            >
              <Grid size={14} />
              <span className="hidden sm:inline">Grid</span>
            </button>

            {/* Keyboard shortcuts modal toggle */}
            <button
              type="button"
              onClick={() => setShowShortcutsModal(true)}
              className="p-2 rounded-md border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted text-xs font-semibold transition"
              title="Keyboard shortcuts (?)"
              aria-label="Keyboard shortcuts"
            >
              <Keyboard size={14} />
            </button>

            {/* Submit Quiz button */}
            <Button
              variant="primary"
              size="sm"
              onClick={handleTriggerSubmit}
              icon={<CheckCircle size={14} />}
              className="font-bold"
            >
              Submit
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div
          role="progressbar"
          aria-valuenow={answeredCount}
          aria-valuemin={0}
          aria-valuemax={questions.length}
          aria-label="Quiz progress"
          className="w-full bg-muted h-1.5 rounded-full overflow-hidden mt-2.5"
        >
          <div
            className="bg-primary h-full transition-all duration-300"
            style={{ width: `${(answeredCount / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* ── Reading Passage / Reference Source Display ── */}
      {q.sourceContent && (
        <div className="rounded-lg border border-border bg-card p-5 space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-wide border-b border-border pb-2">
            <BookOpen size={15} />
            <span>{q.sourceTitle ? formatDisplayTitle(q.sourceTitle) : "Reference passage"}</span>
          </div>
          <div className="text-sm leading-relaxed text-foreground font-serif whitespace-pre-wrap">
            <GeneratedContent normalizeMath={session.source === "exam"}>
              {q.sourceContent}
            </GeneratedContent>
          </div>
        </div>
      )}

      {/* ── Question Card with Passage Layout ── */}
      <QuestionPassageLayout passage={currentPassage}>
        <div className="bg-card rounded-lg border border-border p-6 sm:p-8 space-y-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <SubjectBadge subject={q.subject.replace("Mathematics", "Math")} />
              {isCurrentFlagged && (
                <Badge variant="warning">
                  <Flag size={10} className="mr-1 fill-current" /> Flagged
                </Badge>
              )}
            </div>
            <button
              type="button"
              onClick={() => setReportingQ(q)}
              title="Report an issue with this question"
              className="text-xs text-muted-foreground hover:text-destructive hover:bg-muted p-1.5 rounded-md transition flex items-center gap-1 font-medium"
            >
              <Flag size={13} />
              <span>Report</span>
            </button>
          </div>

          {q.instruction && (
            <div className="rounded-md border border-primary/20 bg-secondary/40 p-3.5 text-xs sm:text-sm font-medium text-foreground leading-relaxed">
              <GeneratedContent inline normalizeMath={session.source === "exam"}>
                {q.instruction}
              </GeneratedContent>
            </div>
          )}

          {/* Question Text */}
          <div className="text-base sm:text-lg font-semibold text-foreground leading-relaxed">
            <span className="text-primary font-bold mr-2">Q{current + 1}.</span>
            <MathText>{q.question}</MathText>
          </div>

          {/* Question Figure */}
          {q.figure_image && (
            <QuestionFigure
              figureImage={q.figure_image}
              figureAlt={q.figure_alt}
              figureCaption={q.figure_alt}
            />
          )}

          {/* ── Options Display with large radio-style buttons (min 48px) ── */}
          {qType === "true_false" ? (
            /* True / False Options */
            <div className="grid grid-cols-2 gap-4 pt-2" role="radiogroup" aria-label="True or False choices">
              {q.options.map((opt, i) => {
                const isSelected = selected === i;
                return (
                  <button
                    key={i}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => selectOption(i)}
                    className={`min-h-[52px] p-4 rounded-md border-2 text-center font-bold text-base transition flex flex-col items-center justify-center gap-1.5 focus-visible:ring-2 focus-visible:ring-primary ${
                      isSelected
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card hover:bg-muted text-foreground"
                    }`}
                  >
                    <span className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">
                      Option {i === 0 ? "A" : "B"}
                    </span>
                    <MathText inline>{opt}</MathText>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Multiple Choice Options */
            <div className="space-y-3 pt-2" role="radiogroup" aria-label="Question answer options">
              {q.options.map((opt, i) => {
                const label = q.optionLabels?.[i] || String.fromCharCode(65 + i);
                const isSelected = selected === i;

                return (
                  <button
                    key={i}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => selectOption(i)}
                    className={`w-full text-left min-h-[48px] p-4 rounded-md border-2 transition flex items-start gap-3.5 focus-visible:ring-2 focus-visible:ring-primary ${
                      isSelected
                        ? "border-primary bg-primary/5 font-semibold text-foreground"
                        : "border-border bg-card hover:bg-muted text-foreground"
                    }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-md text-xs font-bold flex items-center justify-center shrink-0 mt-0.5 ${
                        isSelected
                          ? "bg-primary text-on-accent"
                          : "bg-muted text-muted-foreground border border-border"
                      }`}
                    >
                      {label}
                    </span>
                    <div className="flex-1 pt-0.5 text-sm sm:text-base leading-relaxed">
                      <MathText inline>{opt}</MathText>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </QuestionPassageLayout>

      {/* ── Fixed Bottom Navigation Footer ── */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <Button
          variant="secondary"
          size="md"
          onClick={() => {
            if (current > 0) changeQuestion(current - 1);
          }}
          disabled={current === 0}
          icon={<ArrowLeft size={16} />}
        >
          Previous
        </Button>

        <div className="text-xs font-medium text-muted-foreground text-center">
          {answeredCount} of {questions.length} answered
        </div>

        {current < questions.length - 1 ? (
          <Button
            variant="primary"
            size="md"
            onClick={() => changeQuestion(current + 1)}
            icon={<ArrowRight size={16} />}
            iconPosition="right"
          >
            Next
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            onClick={handleTriggerSubmit}
            icon={<CheckCircle size={16} />}
          >
            Submit
          </Button>
        )}
      </div>

      {/* ── Question Navigator Grid Modal ── */}
      <Modal
        isOpen={showNavigator}
        onClose={() => setShowNavigator(false)}
        size="md"
        title="Question Navigator"
        description={`${answeredCount} of ${questions.length} answered • ${flaggedQuestions.size} flagged`}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-xs text-muted-foreground border-b border-border pb-3">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-xs bg-primary" />
              <span>Current</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-xs bg-success" />
              <span>Answered</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-xs bg-warning text-warning-foreground" />
              <span>Flagged</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-xs bg-muted border border-border" />
              <span>Unanswered</span>
            </div>
          </div>

          <div className="grid grid-cols-5 sm:grid-cols-8 gap-2 max-h-80 overflow-y-auto p-1">
            {questions.map((_, idx) => {
              const isAnswered = userAnswers[idx] !== null;
              const isFlagged = flaggedQuestions.has(idx);
              const isCurr = current === idx;

              let style = "bg-muted border-border text-muted-foreground hover:bg-muted/80";
              if (isCurr) {
                style = "ring-2 ring-primary border-primary bg-primary/10 text-primary font-bold";
              } else if (isFlagged) {
                style = "bg-warning/20 border-warning text-warning-foreground font-semibold";
              } else if (isAnswered) {
                style = "bg-success/15 border-success/40 text-success font-semibold";
              }

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    changeQuestion(idx);
                    setShowNavigator(false);
                  }}
                  aria-label={`Question ${idx + 1}${isAnswered ? ", answered" : ""}${
                    isFlagged ? ", flagged" : ""
                  }`}
                  className={`relative p-2.5 rounded-md border text-xs flex flex-col items-center justify-center transition ${style}`}
                >
                  <span>{idx + 1}</span>
                  {isFlagged && (
                    <Flag size={9} className="absolute top-1 right-1 fill-current" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="pt-2 flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowNavigator(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Keyboard Shortcuts Modal ── */}
      <Modal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
        size="sm"
        title="Keyboard Shortcuts"
        description="Speed up your practice with standard keyboard controls"
      >
        <div className="space-y-3 text-xs">
          <div className="flex items-center justify-between p-2 rounded-md bg-muted/40 border border-border">
            <span className="text-muted-foreground">Select option A, B, C, D</span>
            <div className="flex gap-1">
              <kbd className="px-2 py-0.5 rounded bg-card border border-border font-mono font-bold">1-4</kbd>
              <span className="text-muted-foreground">or</span>
              <kbd className="px-2 py-0.5 rounded bg-card border border-border font-mono font-bold">A-D</kbd>
            </div>
          </div>
          <div className="flex items-center justify-between p-2 rounded-md bg-muted/40 border border-border">
            <span className="text-muted-foreground">Next question</span>
            <div className="flex gap-1">
              <kbd className="px-2 py-0.5 rounded bg-card border border-border font-mono font-bold">→</kbd>
              <span className="text-muted-foreground">or</span>
              <kbd className="px-2 py-0.5 rounded bg-card border border-border font-mono font-bold">Enter</kbd>
            </div>
          </div>
          <div className="flex items-center justify-between p-2 rounded-md bg-muted/40 border border-border">
            <span className="text-muted-foreground">Previous question</span>
            <kbd className="px-2 py-0.5 rounded bg-card border border-border font-mono font-bold">←</kbd>
          </div>
          <div className="flex items-center justify-between p-2 rounded-md bg-muted/40 border border-border">
            <span className="text-muted-foreground">Flag / unflag question</span>
            <kbd className="px-2 py-0.5 rounded bg-card border border-border font-mono font-bold">F</kbd>
          </div>
          <div className="flex items-center justify-between p-2 rounded-md bg-muted/40 border border-border">
            <span className="text-muted-foreground">Toggle shortcuts help</span>
            <kbd className="px-2 py-0.5 rounded bg-card border border-border font-mono font-bold">?</kbd>
          </div>
          <div className="pt-2 flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowShortcutsModal(false)}>
              Got it
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Leave Quiz Confirmation Dialog ── */}
      <ConfirmDialog
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={handleLeaveQuiz}
        title="Leave quiz?"
        message="Your progress is saved on this device. You can return and continue this quiz later."
        confirmLabel="Leave quiz"
        cancelLabel="Keep practicing"
        variant="destructive"
      />

      {/* ── Submit Confirmation Dialog ── */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        size="sm"
        title={session.source === "exam" ? "Submit exam" : "Submit quiz"}
        description={`You have answered ${answeredCount} out of ${questions.length} questions.`}
      >
        <div className="space-y-4">
          {questions.length - answeredCount > 0 ? (
            <div className="rounded-md bg-primary/10 border border-primary/25 p-3 text-xs text-foreground space-y-1">
              <p className="font-bold text-primary">
                {questions.length - answeredCount} unanswered{" "}
                {questions.length - answeredCount === 1 ? "question" : "questions"} remaining
              </p>
              <p className="text-muted-foreground text-xs">
                Unanswered questions will be scored as incorrect.
              </p>
            </div>
          ) : (
            <div className="rounded-md bg-success/10 border border-success/30 p-3 text-xs text-success font-medium">
              All questions answered. Ready to review your score!
            </div>
          )}

          <div className="flex items-center gap-2.5 pt-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowConfirmModal(false)}
            >
              Continue quiz
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                setShowConfirmModal(false);
                void finalizeAndNavigate();
              }}
            >
              Submit now
            </Button>
          </div>
        </div>
      </Modal>

      {reportingQ && (
        <ReportQuestionModal
          questionId={reportingQ.id}
          questionText={reportingQ.question}
          isOpen={true}
          onClose={() => setReportingQ(null)}
        />
      )}
    </div>
    </FocusShell>
  );
}

export function QuizResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = useQuizRoutes();
  const [showDetails, setShowDetails] = useState(true);
  const [onlyMistakes, setOnlyMistakes] = useState(false);
  const [reportingQ, setReportingQ] = useState<QuizQuestion | null>(null);
  const session = useMemo(() => loadQuizSession(), []);
  const state = location.state as {
    answers?: number[];
    title?: string;
    subjectSlug?: string;
    subject?: string;
  } | null;
  const questions: QuizQuestion[] = session?.questions ?? [];
  const answers: number[] = state?.answers?.length
    ? state.answers
    : (session?.answers ?? []);

  useEffect(() => {
    if (!questions.length || answers.length !== questions.length) {
      navigate(routes.home, { replace: true });
    }
  }, [questions.length, answers.length, navigate, routes.home]);

  if (!questions.length || answers.length !== questions.length) return null;

  const score = answers.filter((a, i) => a >= 0 && a === questions[i]?.correct).length;
  const incorrectCount = questions.length - score;
  const pct = Math.round((score / questions.length) * 100);

  // Per-section / per-topic breakdown
  const sectionBreakdown = useMemo(() => {
    const groups: Record<string, { total: number; correct: number }> = {};
    questions.forEach((q, idx) => {
      const key = q.topic || q.subject || "General";
      if (!groups[key]) groups[key] = { total: 0, correct: 0 };
      groups[key].total += 1;
      if (answers[idx] === q.correct) {
        groups[key].correct += 1;
      }
    });
    return Object.entries(groups).map(([title, stats]) => ({
      title,
      ...stats,
      pct: Math.round((stats.correct / stats.total) * 100),
    }));
  }, [questions, answers]);

  const subjectSlug =
    state?.subjectSlug ||
    session?.subjectSlug ||
    (session?.subject ? slugifySubject(session.subject) : "") ||
    (questions[0]?.subject ? slugifySubject(questions[0].subject) : "");

  const isExam = session?.source === "exam";

  const backUrl = isExam
    ? subjectSlug
      ? `/student/study/${subjectSlug}/exams`
      : "/student/study"
    : subjectSlug
    ? `/student/study/${subjectSlug}/quiz`
    : "/student/study";

  const backLabel = isExam ? "Back to past exams" : "Back to quiz";
  const goBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(backUrl);
    }
  };

  // Keep each question with its own position: ids are not unique (exam sections restart their numbering), so the
  // student's answer must be read by position, never by looking the id up again.
  const filteredQuestions = questions
    .map((q, index) => ({ q, index }))
    .filter(({ q, index }) => !onlyMistakes || answers[index] !== q.correct);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground">
          {isExam ? "Exam complete!" : "Quiz complete!"}
        </h2>
        <p className="text-muted-foreground mt-1">
          {formatDisplayTitle(state?.title || session?.title) ||
            (isExam ? "Practice exam" : "Practice quiz")}
        </p>
        <p className="text-muted-foreground text-sm mt-1">
          {score === questions.length
            ? "Perfect score! Outstanding work."
            : score >= questions.length * 0.7
            ? "Well done, keep it up!"
            : "Keep practicing! Review your mistakes below."}
        </p>
      </div>

      {/* ── Score Card ── */}
      <div className="bg-card rounded-lg border border-border p-8 flex flex-col items-center">
        <div className="relative w-32 h-32 mb-4">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="currentColor"
              className="text-muted/20 dark:text-muted/30"
              strokeWidth="10"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="var(--primary)"
              strokeWidth="10"
              strokeDasharray={`${(2 * Math.PI * 42 * pct) / 100} ${2 * Math.PI * 42}`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl font-bold text-foreground">{pct}%</span>
          </div>
        </div>
        <p className="font-semibold text-lg text-foreground">
          {score} / {questions.length} correct
        </p>
        <div className="flex gap-8 mt-6 pt-6 border-t border-border w-full justify-center">
          <div className="text-center">
            <p className="text-2xl font-bold text-success">{score}</p>
            <p className="text-xs text-muted-foreground font-medium">Correct</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-destructive">{incorrectCount}</p>
            <p className="text-xs text-muted-foreground font-medium">Incorrect</p>
          </div>
        </div>
      </div>

      {/* ── Topic / Section Breakdown ── */}
      {sectionBreakdown.length > 1 && (
        <div className="bg-card rounded-lg border border-border p-5 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Topic breakdown
          </h3>
          <div className="space-y-2.5">
            {sectionBreakdown.map((sec) => (
              <div key={sec.title} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground truncate max-w-xs sm:max-w-md">
                    {sec.title}
                  </span>
                  <span className="font-mono font-bold text-muted-foreground">
                    {sec.correct}/{sec.total} ({sec.pct}%)
                  </span>
                </div>
                <div className="w-full bg-muted h-1.5 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      sec.pct >= 70 ? "bg-success" : sec.pct >= 40 ? "bg-warning" : "bg-destructive"
                    }`}
                    style={{ width: `${sec.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Review Answers Toggle Bar with Mistake Filter ── */}
      <div className="bg-card rounded-lg p-4 border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="flex items-center gap-2 text-foreground font-bold text-sm text-left"
        >
          <span>Review answers</span>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 text-muted-foreground ${
              showDetails ? "rotate-180" : ""
            }`}
          />
        </button>

        {incorrectCount > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant={!onlyMistakes ? "primary" : "secondary"}
              size="sm"
              onClick={() => {
                setShowDetails(true);
                setOnlyMistakes(false);
              }}
            >
              All ({questions.length})
            </Button>
            <Button
              variant={onlyMistakes ? "primary" : "secondary"}
              size="sm"
              onClick={() => {
                setShowDetails(true);
                setOnlyMistakes(true);
              }}
              icon={<Filter size={13} />}
            >
              Mistakes only ({incorrectCount})
            </Button>
          </div>
        )}
      </div>

      {showDetails && (
        <div className="space-y-4">
          {filteredQuestions.map(({ q, index: originalIndex }) => {
            const userAns = answers[originalIndex];
            const isCorrect = userAns === q.correct;
            const isUnanswered = userAns == null || userAns < 0;
            const yourText = !isUnanswered ? q.options[userAns!] : undefined;
            const pId = q.passageId || q.passage_id;
            const passage = pId && session?.passages ? session.passages[pId] : null;

            return (
              <QuestionPassageLayout key={`${originalIndex}-${q.id}`} passage={passage} variant="multi">
                <div
                  className={`rounded-lg p-5 border transition-all ${
                    isCorrect
                      ? "border-success/30 bg-success/10/70"
                      : "border-destructive/30 bg-destructive/10/70"
                  }`}
                >
                  {/* Status header with icon + text */}
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
                          isCorrect
                            ? "bg-success/15 text-success"
                            : "bg-destructive/15 text-destructive"
                        }`}
                      >
                        {isCorrect ? (
                          <>
                            <CheckCircle size={14} className="text-success" />
                            <span>Correct</span>
                          </>
                        ) : (
                          <>
                            <XCircle size={14} className="text-destructive" />
                            <span>{isUnanswered ? "Not answered" : "Incorrect"}</span>
                          </>
                        )}
                      </span>
                      <span className="text-xs font-semibold text-muted-foreground">
                        Question {originalIndex + 1} of {questions.length}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setReportingQ(q)}
                      className="text-xs text-muted-foreground hover:text-destructive hover:bg-muted/60 px-2 py-1 rounded-md transition flex items-center gap-1 font-medium"
                      title="Report this question"
                    >
                      <Flag size={12} />
                      <span>Report</span>
                    </button>
                  </div>

                  {/* Question Text */}
                  <div className="text-sm sm:text-base font-semibold text-foreground leading-relaxed mb-3">
                    <MathText>{q.question}</MathText>
                  </div>

                  {/* Question Figure */}
                  {q.figure_image && (
                    <div className="mb-3">
                      <QuestionFigure
                        figureImage={q.figure_image}
                        figureAlt={q.figure_alt}
                        figureCaption={q.figure_alt}
                      />
                    </div>
                  )}

                  {/* Answers Section */}
                  <div className="space-y-2.5">
                    {!isCorrect && (
                      <div className="p-3 rounded-md border text-xs sm:text-sm bg-destructive/10 border-destructive/25">
                        <span className="text-xs font-bold uppercase tracking-wider text-destructive block mb-1">
                          Your answer:
                        </span>
                        <div className="text-destructive font-medium">
                          {isUnanswered ? (
                            <span className="italic opacity-80">Not answered</span>
                          ) : yourText === undefined ? (
                            <span className="italic opacity-80">Your answer unavailable</span>
                          ) : (
                            <MathText inline>{yourText}</MathText>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="p-3 rounded-md border text-xs sm:text-sm bg-success/10 border-success/25">
                      <span className="text-xs font-bold uppercase tracking-wider text-success block mb-1">
                        Correct answer:
                      </span>
                      <div className="text-success font-medium">
                        <MathText inline>{q.options[q.correct]}</MathText>
                      </div>
                    </div>

                    {q.explanation && (
                      <div className="p-3.5 rounded-md border border-border/80 bg-card text-xs text-foreground space-y-1 mt-2">
                        <div className="font-bold text-primary flex items-center gap-1.5 text-xs uppercase tracking-wide">
                          <span>Explanation</span>
                        </div>
                        <div className="text-muted-foreground leading-relaxed">
                          <MathText>{q.explanation}</MathText>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </QuestionPassageLayout>
            );
          })}
        </div>
      )}

      {/* ── Footer Navigation Buttons ── */}
      <div className="flex gap-4 flex-col sm:flex-row pt-2">
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            clearQuizSession();
            if (session?.subject) clearCachedSession(`quiz:${session.subject}`);
            if (session?.title) clearCachedSession(`quiz:${session.title}`);
            goBack();
          }}
          icon={isExam ? <Trophy size={16} /> : <Brain size={16} />}
          className="flex-1"
        >
          {backLabel}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => {
            clearQuizSession();
            if (session?.subject) clearCachedSession(`quiz:${session.subject}`);
            if (session?.title) clearCachedSession(`quiz:${session.title}`);
            navigate(subjectSlug ? `/student/study/${subjectSlug}` : "/student/study");
          }}
          className="flex-1"
        >
          Back to course
        </Button>
      </div>

      {reportingQ && (
        <ReportQuestionModal
          questionId={reportingQ.id}
          questionText={reportingQ.question}
          isOpen={true}
          onClose={() => setReportingQ(null)}
        />
      )}
    </div>
  );
}
